import React from 'react';
import { Check, Minus, Loader2, Send, ThumbsUp, Undo2, Clock3 } from 'lucide-react';
import { Button } from '../ui/button';

export const monthLabel = (m) => new Date(`${m}-01T12:00:00`).toLocaleDateString(undefined, { month: 'short', year: 'numeric' });

const Cell = ({ row, col, onTick, locked }) => {
  const auto = row.autoNa.includes(col.key);
  const st = auto ? 'na' : row.items[col.key]?.state;
  const next = st === 'done' ? 'na' : st === 'na' ? null : 'done';
  const cls = st === 'done' ? 'bg-emerald-600 border-emerald-600 text-white' : st === 'na' ? 'bg-stone-100 border-stone-200 text-stone-400' : 'bg-white border-stone-300 hover:border-emerald-600';
  const title = auto ? 'Not applicable for this client' : row.items[col.key] ? `${st === 'done' ? 'Done' : 'N/A'} · ${row.items[col.key].by || ''}` : 'Click to tick off';
  return (
    <td className="px-2 py-2 text-center border-l border-stone-100">
      <button type="button" disabled={auto || locked} onClick={() => onTick(row, col.key, next)} title={title}
        className={`h-7 w-7 rounded-md border grid place-items-center mx-auto transition-[background-color,border-color,transform] active:scale-90 disabled:cursor-not-allowed ${cls}`}
        data-testid={`bk-cell-${row.clientId}-${col.key}`}>
        {st === 'done' ? <Check size={15} strokeWidth={3}/> : st === 'na' ? <span className="text-[9px] font-bold">N/A</span> : null}
      </button>
    </td>
  );
};

const Action = ({ row, isOwner, busy, onAction }) => {
  if (busy) return <Loader2 size={16} className="animate-spin text-stone-400 mx-auto"/>;
  if (row.status === 'submitted') {
    return isOwner ? (
      <div className="flex gap-1.5 justify-center">
        <Button size="sm" className="h-8 bg-emerald-800 hover:bg-emerald-900" onClick={() => onAction(row, 'approve')} data-testid={`bk-approve-${row.clientId}`}><ThumbsUp size={13} className="mr-1"/>Approve</Button>
        <Button size="sm" variant="outline" className="h-8" onClick={() => onAction(row, 'reject')} data-testid={`bk-reject-${row.clientId}`}><Undo2 size={13}/></Button>
      </div>
    ) : <span className="inline-flex items-center gap-1 text-xs text-violet-700 font-medium" data-testid={`bk-awaiting-${row.clientId}`}><Clock3 size={13}/>Awaiting partner</span>;
  }
  return (
    <Button size="sm" disabled={!row.complete} className="h-8 bg-emerald-800 hover:bg-emerald-900" onClick={() => onAction(row, 'submit')} data-testid={`bk-submit-${row.clientId}`}>
      <Send size={13} className="mr-1"/>Send for approval
    </Button>
  );
};

export const BookkeepingGrid = ({ columns, rows, isOwner, busyId, onTick, onAction }) => (
  <div className="rounded-xl bg-white border border-stone-200 overflow-x-auto" data-testid="bk-grid">
    <table className="text-sm min-w-max w-full">
      <thead className="bg-stone-50">
        <tr>
          <th className="sticky left-0 z-10 bg-stone-50 text-left px-4 py-3 min-w-[110px] sm:min-w-[220px] text-xs uppercase tracking-wide text-stone-500">Client · month</th>
          {columns.map((c) => <th key={c.key} className="px-2 py-3 w-[92px] align-bottom border-l border-stone-100"><div className="text-[10px] uppercase tracking-wide text-stone-400">{c.group}</div><div className="text-[11px] font-medium text-stone-700 leading-tight">{c.label}</div></th>)}
          <th className="sm:sticky right-0 z-10 bg-stone-50 px-3 py-3 text-xs uppercase tracking-wide text-stone-500 min-w-[190px] border-l border-stone-200 sm:shadow-[-6px_0_8px_-6px_rgba(0,0,0,0.12)]">Progress · approval</th>
        </tr>
      </thead>
      <tbody>
        {rows.map((r) => (
          <tr key={r.clientId} className={`border-t border-stone-100 ${r.status === 'submitted' ? 'bg-violet-50/40' : ''}`} data-testid={`bk-row-${r.clientId}`}>
            <td className="sticky left-0 z-10 bg-white px-3 sm:px-4 py-2 max-w-[120px] sm:max-w-none">
              <div className="font-medium text-stone-900">{r.clientName}</div>
              <div className="text-xs text-stone-500" data-testid={`bk-month-${r.clientId}`}>{monthLabel(r.month)}{r.entityType ? ` · ${r.entityType}` : ''}</div>
              {r.rejectNote && r.status === 'open' && <div className="text-[11px] text-red-600 mt-0.5" data-testid={`bk-reject-note-${r.clientId}`}>Sent back: {r.rejectNote}</div>}
            </td>
            {columns.map((c) => <Cell key={c.key} row={r} col={c} onTick={onTick} locked={r.status === 'submitted' || busyId === r.clientId}/>)}
            <td className="sm:sticky right-0 z-10 bg-white px-3 py-2 text-center border-l border-stone-200 sm:shadow-[-6px_0_8px_-6px_rgba(0,0,0,0.12)]">
              <div className="flex items-center justify-center gap-2 mb-1.5">
                <span className="text-xs font-semibold tabular-nums text-stone-700" data-testid={`bk-progress-${r.clientId}`}>{r.done}/{r.total}</span>
                <div className="h-1.5 w-16 rounded-full bg-stone-100 overflow-hidden"><div className="h-full bg-emerald-600 transition-[width]" style={{ width: `${(r.done / r.total) * 100}%` }}/></div>
              </div>
              <Action row={r} isOwner={isOwner} busy={busyId === r.clientId} onAction={onAction}/>
            </td>
          </tr>
        ))}
      </tbody>
    </table>
  </div>
);
